import type { ParsedRow } from './import-validation';

export interface ParsedWorkbook {
  /** All sheet names, in workbook order — for the sheet picker when > 1. */
  sheetNames: string[];
  /** The sheet these rows/headers came from. */
  sheetName: string;
  /** Ordered header names from the sheet's first row (blank headers dropped). */
  headers: string[];
  /** Data rows keyed by header. Blank cells are `null` so mapped-blank clears work. */
  rows: ParsedRow[];
}

/**
 * Parse an uploaded spreadsheet client-side with SheetJS. Lazy-imported so the
 * (large) `xlsx` bundle only loads when a user actually opens the importer.
 *
 * `cellDates: true` yields JS `Date` objects for date-typed cells (the
 * importer's date coercion handles them); other numeric cells stay numbers, so
 * we deliberately avoid the fragile "any number in the Excel-epoch range is a
 * date" heuristic that would misclassify legitimate numeric columns.
 */
export async function parseWorkbook(file: File, sheetIndex = 0): Promise<ParsedWorkbook> {
  const XLSX = await import('xlsx');

  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { cellDates: true, cellFormula: false });

  const sheetNames = wb.SheetNames;
  const sheetName = sheetNames[sheetIndex] ?? sheetNames[0];
  if (!sheetName) return { sheetNames, sheetName: '', headers: [], rows: [] };

  const sheet = wb.Sheets[sheetName];
  if (!sheet) return { sheetNames, sheetName, headers: [], rows: [] };

  // Read as a matrix so we control header/row keying ourselves. Building rows
  // from the matrix (rather than a second `sheet_to_json` keyed by the raw
  // header cells) guarantees `row[header]` uses the SAME trimmed header names
  // the mapping UI sees — otherwise a trailing space or a blank spacer column
  // would silently break `row[entry.excelHeader]` lookups.
  const matrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    blankrows: false,
    defval: null,
  });

  // Column index → trimmed header name. Blank headers are dropped, but the
  // original index is preserved so data cells stay aligned to their column.
  const columns: { index: number; name: string }[] = [];
  (matrix[0] ?? []).forEach((cell, index) => {
    const name = headerName(cell);
    if (name !== '') columns.push({ index, name });
  });
  const headers = columns.map((c) => c.name);

  // Data rows keyed by the trimmed headers. Blank cells become `null` so a
  // mapped-but-blank column clears the target rather than being omitted.
  const rows: ParsedRow[] = matrix.slice(1).map((dataRow) => {
    const row: ParsedRow = {};
    for (const col of columns) row[col.name] = dataRow[col.index] ?? null;
    return row;
  });

  return { sheetNames, sheetName, headers, rows };
}

/** Trimmed header name from a first-row cell; non-string/number cells → ''. */
function headerName(cell: unknown): string {
  if (typeof cell === 'string') return cell.trim();
  if (typeof cell === 'number') return String(cell);
  return '';
}
